import { Avatar, Badge, Group, Text } from '@mantine/core';
import { useMemo } from 'react';
import { UnifyTable, UnifyTableSortAffordance, type UnifyTableColumn } from '@/components/UnifyTable';
import { Practice } from '../types/rbac';

export type PracticeSortColumn = 'name' | 'ownerName' | 'userCount' | 'createdAt';

interface PracticeTableProps {
  practices: Practice[];
  onViewSettings: (practice: Practice) => void;
  sortColumn: PracticeSortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: PracticeSortColumn) => void;
}

const getStatusColor = (status: string) => {
  if (status === 'Active') return 'green';
  return 'red';
};

export function PracticeTable({
  practices,
  onViewSettings,
  sortColumn,
  sortDirection,
  onSort,
}: PracticeTableProps) {
  const columns = useMemo((): UnifyTableColumn<Practice>[] => {
    const sortableHeader = (col: PracticeSortColumn, label: string) => {
      const active = sortColumn === col;
      return (
        <Group
          gap={6}
          wrap="nowrap"
          onClick={() => onSort(col)}
          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        >
          <Text component="span" size="sm" fw={600}>
            {label}
          </Text>
          <UnifyTableSortAffordance active={active} direction={sortDirection} />
        </Group>
      );
    };

    return [
      {
        key: 'name',
        label: 'Practice Name',
        renderHeader: () => sortableHeader('name', 'Practice Name'),
        render: (practice) => (
          <Group gap="sm">
            <Avatar radius="md" size="sm" color="gold">
              {practice.name.charAt(0).toUpperCase()}
            </Avatar>
            <Text size="sm" fw={500}>
              {practice.name}
            </Text>
          </Group>
        ),
        getValue: (p) => p.name,
      },
      {
        key: 'ownerName',
        label: 'Practice Owner',
        renderHeader: () => sortableHeader('ownerName', 'Practice Owner'),
        render: (practice) => (
          <Text size="sm" c="gray.6">
            {practice.ownerName}
          </Text>
        ),
        getValue: (p) => p.ownerName,
      },
      {
        key: 'userCount',
        label: 'Users',
        renderHeader: () => sortableHeader('userCount', 'Users'),
        render: (practice) => (
          <Text size="sm" c="gray.6">
            {practice.userCount}
          </Text>
        ),
        getValue: (p) => String(p.userCount),
      },
      {
        key: 'status',
        label: 'Status',
        render: (practice) => (
          <Badge color={getStatusColor(practice.status)} variant="light" size="sm">
            {practice.status}
          </Badge>
        ),
        getValue: (p) => p.status,
      },
      {
        key: 'createdAt',
        label: 'Created',
        renderHeader: () => sortableHeader('createdAt', 'Created'),
        render: (practice) => (
          <Text size="sm" c="gray.6">
            {practice.createdAt}
          </Text>
        ),
        getValue: (p) => p.createdAt,
      },
    ];
  }, [onSort, sortColumn, sortDirection]);

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={practices}
      getRowKey={(p) => p.id}
      pagination
      defaultRowsPerPage={25}
      onRowClick={onViewSettings}
    />
  );
}
